import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import {
  StepFormContainer,
  StepOneOrganizerSelection,
  StepTwoEventInformation,
  StepThreeEventTickets,
  StepFourReviewEvent,
} from "./components";
import { transformEventPayload } from "./utils/payloadTransformer";
import eventService from "../../../../api/EventService";

const CreateEvent = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    organizer: null,
    // Step 2 data
    eventName: "",
    eventType: [],
    eventStartDate: "",
    eventEndDate: "",
    eventStartTime: "",
    eventEndTime: "",
    venueName: "",
    venueStreetAddress: "",
    country: "",
    city: "",
    venueGoogleLocation: "",
    thumbnail: null,
    featuredImages: [],
    aboutEvent: "",
    performers: [],
    sponsors: [],
    agenda: [],
    // Step 3 data
    overallSlots: "",
    ticketTypes: [],
  });

  const totalSteps = 4;
  const stepTitles = [
    "Select Organizer",
    "Event Information",
    "Event Tickets",
    "Review & Submit",
  ];

  const handleOrganizerSelect = (organizer) => {
    setFormData((prev) => ({
      ...prev,
      organizer,
    }));
  };

  const handleStepOneNext = () => {
    if (formData.organizer) {
      setCurrentStep(2);
    }
  };

  const handleStepTwoBack = () => {
    setCurrentStep(1);
  };

  const handleStepTwoNext = () => {
    setCurrentStep(3);
  };

  const handleStepThreeBack = () => {
    setCurrentStep(2);
  };

  const handleStepThreeNext = () => {
    setCurrentStep(4);
  };

  const handleStepFourBack = () => {
    setCurrentStep(3);
  };

  const handleSubmitEvent = async (data) => {
    const payload = transformEventPayload(data);

    if (!payload.thumbnail) {
      toast.error("Please upload a thumbnail image");
      return;
    }

    try {
      setIsLoading(true);
      const response = await eventService.createEvent(payload);

      if (response.status && response.data?.status_code === 200) {
        toast.success(
          response.data.reason ||
            response.message ||
            "Event created successfully!"
        );
        navigate("/admin/media/events");
      } else {
        let errorMessage = response.message || "Failed to create event";

        if (response.errors) {
          const fieldErrors = Object.values(response.errors).flat();
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join(", ");
          }
        }

        toast.error(errorMessage);
      }
    } catch (err) {
      const errorData = err?.response?.data;
      let errorMessage = "An error occurred while creating event";

      if (errorData) {
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.reason) {
          errorMessage = errorData.reason;
        }

        // Include field-specific errors if available
        if (errorData.errors) {
          const fieldErrors = Object.values(errorData.errors).flat();
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join(", ");
          }
        }
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormDataUpdate = (updatedData) => {
    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="mb-6 px-6 py-4">
        <button
          onClick={() => navigate("/admin/media/events")}
          className="flex items-center mb-3 text-gray-600 transition-colors hover:text-gray-900 group"
        >
          <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back to Events</span>
        </button>

        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Create New Event
            </h1>
            <p className="mt-0.5 text-xs text-gray-600">
              Follow the steps to create a new event
            </p>
          </div>
        </div>
      </div>

      <StepFormContainer
        currentStep={currentStep}
        totalSteps={totalSteps}
        stepTitles={stepTitles}
        showProgress={true}
      >
        {/* Step Content */}
        {currentStep === 1 && (
          <StepOneOrganizerSelection
            selectedOrganizer={formData.organizer}
            onSelect={handleOrganizerSelect}
            onNext={handleStepOneNext}
          />
        )}

        {currentStep === 2 && (
          <StepTwoEventInformation
            formData={formData}
            onUpdate={handleFormDataUpdate}
            onBack={handleStepTwoBack}
            onNext={handleStepTwoNext}
          />
        )}

        {currentStep === 3 && (
          <StepThreeEventTickets
            formData={formData}
            onUpdate={handleFormDataUpdate}
            onBack={handleStepThreeBack}
            onNext={handleStepThreeNext}
          />
        )}

        {currentStep === 4 && (
          <StepFourReviewEvent
            formData={formData}
            onBack={handleStepFourBack}
            onSubmit={handleSubmitEvent}
          />
        )}
      </StepFormContainer>
    </>
  );
};

export default CreateEvent;
